import React, { useState } from 'react';
import { X, Check, CreditCard, Shield, Loader2, PartyPopper } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { getDeviceId } from '../utils/storage';

interface UpgradePremiumProps {
  isOpen: boolean;
  onClose: () => void;
}

interface WaitlistForm {
  email: string;
  name: string;
}

const UpgradePremium: React.FC<UpgradePremiumProps> = ({ isOpen, onClose }) => {
  const [selectedPlan, setSelectedPlan] = useState<'free' | 'premium'>('premium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState<WaitlistForm>({
    email: '',
    name: ''
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: ''
  });

  const handleJoinWaitlist = async () => {
    // Reset errors
    setFormErrors({ name: '', email: '' });

    // Validate inputs
    let hasErrors = false;
    if (!waitlistForm.name.trim()) {
      setFormErrors(prev => ({ ...prev, name: 'Name is required' }));
      hasErrors = true;
    }

    if (!waitlistForm.email.trim()) {
      setFormErrors(prev => ({ ...prev, email: 'Email is required' }));
      hasErrors = true;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(waitlistForm.email)) {
      setFormErrors(prev => ({ ...prev, email: 'Please enter a valid email' }));
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading('Adding you to the waitlist...');

    try {
      const deviceId = await getDeviceId();
      
      // First set the device_id
      const { error: deviceError } = await supabase.rpc('set_device_id', {
        device_id: deviceId
      });

      if (deviceError) throw deviceError;

      // Then insert into waitlist
      const { error } = await supabase
        .from('premium_waitlist')
        .insert([
          {
            email: waitlistForm.email.trim(),
            name: waitlistForm.name.trim(),
            device_id: deviceId,
            notification_preferences: {
              email: true,
              push: true,
              sms: false
            },
            status: 'pending'
          }
        ])
        .select()
        .single();

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          toast.dismiss(loadingToast);
          setFormErrors(prev => ({ 
            ...prev, 
            email: 'This email is already on the waitlist' 
          }));
          return;
        }
        throw error;
      }

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      // Show success animation and toast
      setShowSuccess(true);
      
      // Show success notifications
      toast.success('Successfully joined the waitlist! 🎉', {
        duration: 3000,
        icon: '✨'
      });
      
      setTimeout(() => {
        toast.success('You will be notified when premium launches', {
          duration: 4000,
          icon: '📬',
          style: {
            background: '#dcfce7',
            color: '#166534'
          }
        });
      }, 1000);
      
      setTimeout(() => {
        toast.success('Thank you for your interest in Angel Premium!', {
          duration: 5000,
          icon: '💫',
          style: {
            background: '#e0f2fe',
            color: '#1e40af'
          }
        });
      }, 2000);

      // Close modal after success animation
      setTimeout(() => {
        onClose();
        setShowSuccess(false);
      }, 3000);

    } catch (error: unknown) {
      toast.dismiss(loadingToast);
      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred";
      console.error("Error joining waitlist:", errorMessage);
      toast.error('Failed to join waitlist. Please try again.', {
        icon: '❌',
        style: { background: '#fee2e2', color: '#991b1b' },
        duration: 4000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overscroll-contain">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden my-auto max-h-[calc(100vh-2rem)] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold text-gray-800">Upgrade to Premium</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Close upgrade modal"
            >
              <X className="h-6 w-6 text-gray-500" />
            </button>
          </div>
          <p className="mt-2 text-gray-600">
            Unlock premium features and enjoy an ad-free experience.
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-6 space-y-8">
          {/* Plan Comparison */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Free Plan */}
            <div
              className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                selectedPlan === 'free'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
              onClick={() => setSelectedPlan('free')}
              role="button"
              tabIndex={0}
              aria-label="Select Free Plan"
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className={`w-4 h-4 rounded-full border-2 ${
                    selectedPlan === 'free'
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                  }`}
                />
                <h3 className="text-lg font-medium text-gray-900">Free Plan</h3>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-4">
                $0<span className="text-base font-normal text-gray-500">/month</span>
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  Basic AI Financial Advice
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  Limited Chat History (7 days)
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  Basic Budget Planning
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  Simple Savings Calculator
                </li>
              </ul>
            </div>

            {/* Premium Plan */}
            <div
              className={`p-4 border-2 rounded-xl cursor-pointer transition-all relative ${
                selectedPlan === 'premium'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
              onClick={() => setSelectedPlan('premium')}
              role="button"
              tabIndex={0}
              aria-label="Select Premium Plan"
            >
              <div className="absolute -top-3 left-4 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                RECOMMENDED
              </div>
              <div className="flex items-center gap-2 mb-2">
                <div
                  className={`w-4 h-4 rounded-full border-2 ${
                    selectedPlan === 'premium'
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                  }`}
                />
                <h3 className="text-lg font-medium text-gray-900">Premium Plan</h3>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-4">
                $5.99<span className="text-base font-normal text-gray-500">/month</span>
                <span className="ml-2 inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                  Coming Soon
                </span>
              </p>
              <ul className="space-y-3">
                {/* Financial Features */}
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Complete Ad-Free Experience</span>
                    <p className="text-xs text-gray-500">No ads or interruptions</p>
                  </div>
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Advanced AI Financial Planning</span>
                    <p className="text-xs text-gray-500">Custom strategies based on your goals</p>
                  </div>
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Priority Support & Response</span>
                    <p className="text-xs text-gray-500">24/7 access to advanced features</p>
                  </div>
                </li>
                {/* Education Features */}
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Advanced Education Tools</span>
                    <p className="text-xs text-gray-500">Interactive learning materials and visualizations</p>
                  </div>
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Personalized Learning Plans</span>
                    <p className="text-xs text-gray-500">Custom study schedules and progress tracking</p>
                  </div>
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Practice Tests & Assessments</span>
                    <p className="text-xs text-gray-500">Unlimited access to practice materials</p>
                  </div>
                </li>
                {/* Direct Care Features */}
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">30-Day Client Summaries</span>
                    <p className="text-xs text-gray-500">Comprehensive monthly client reports</p>
                  </div>
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Client Progress Tracking</span>
                    <p className="text-xs text-gray-500">Detailed behavior and progress analytics</p>
                  </div>
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Care Plan Management</span>
                    <p className="text-xs text-gray-500">Create and track personalized care plans</p>
                  </div>
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Shift Pattern Analysis</span>
                    <p className="text-xs text-gray-500">AI-powered insights from shift data</p>
                  </div>
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Investment Portfolio Analysis</span>
                    <p className="text-xs text-gray-500">Risk assessment and optimization</p>
                  </div>
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Advanced Budget Tools</span>
                    <p className="text-xs text-gray-500">Multi-category tracking & forecasting</p>
                  </div>
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Market Trend Analysis</span>
                    <p className="text-xs text-gray-500">Real-time insights and predictions</p>
                  </div>
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Tax Strategy Planning</span>
                    <p className="text-xs text-gray-500">Personalized tax optimization advice</p>
                  </div>
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Retirement Simulator</span>
                    <p className="text-xs text-gray-500">Advanced retirement planning tools</p>
                  </div>
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">Unlimited Chat History</span>
                    <p className="text-xs text-gray-500">Never lose your financial discussions</p>
                  </div>
                </li>
                <li className="flex items-center text-gray-600">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <div>
                    <span className="font-medium">PDF Report Generation</span>
                    <p className="text-xs text-gray-500">Detailed financial reports & analysis</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Premium Features List */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Premium Features Include:</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start">
                <Shield className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">All-in-One Professional Suite</h4>
                  <p className="text-sm text-gray-600">
                    Finance, Education, and Direct Care tools in one platform
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <CreditCard className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Comprehensive Analytics</h4>
                  <p className="text-sm text-gray-600">
                    Advanced insights across all modes with detailed reporting
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Waitlist Form */}
          {selectedPlan === 'premium' && (
          <div className="bg-blue-50 rounded-lg p-4 mt-4">
            <p className="text-sm text-blue-800 font-medium animate-fade-in">
              🌟 Coming Soon: Get early access to these premium features! Join our waitlist to be
              notified when we launch and receive a special founding member discount.
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-blue-900 mb-1">Name</label>
                <input
                  type="text"
                  value={waitlistForm.name}
                  onChange={(e) =>
                    setWaitlistForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className={`w-full px-4 py-2 bg-white border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    showSuccess ? 'opacity-50' : ''
                  }`}
                  placeholder="Enter your full name"
                  disabled={isSubmitting || showSuccess}
                />
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-900 mb-1">Email</label>
                <input
                  type="email"
                  value={waitlistForm.email}
                  onChange={(e) =>
                    setWaitlistForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                  className={`w-full px-4 py-2 bg-white border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    showSuccess ? 'opacity-50' : ''
                  }`}
                  placeholder="Enter your email"
                  disabled={isSubmitting || showSuccess}
                />
                {formErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                )}
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Payment Section */}
        <div className="p-6 bg-gray-50 space-y-4 border-t border-gray-200 sticky bottom-0">
          {selectedPlan === 'premium' ? (
            <button
              onClick={handleJoinWaitlist}
              disabled={isSubmitting || showSuccess}
              className={`w-full px-6 py-3 rounded-lg transition-all flex items-center justify-center gap-2 touch-manipulation min-h-[44px] ${
                showSuccess
                  ? 'bg-green-500 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white'
              } ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Joining Waitlist...</span>
                </>
              ) : showSuccess ? (
                <>
                  <Check className="h-5 w-5" />
                  <span>Successfully Joined!</span>
                </>
              ) : (
                <>
                  <Shield className="h-5 w-5" />
                  <span>Join Premium Waitlist</span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={onClose}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors flex items-center justify-center gap-2 touch-manipulation min-h-[44px]"
              aria-label="Continue with Free Plan"
            >
              <CreditCard className="h-5 w-5" />
              Continue with Free Plan
            </button>
          )}
          <p className="text-center text-sm text-gray-500">
            Cancel anytime. 30-day money-back guarantee.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradePremium;